import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  MenuItem,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Add as AddIcon, Redeem as PackageIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { ManagementContentSkeleton } from '../layout/ManagementPageSkeleton';
import ConfirmDialog from '../common/ConfirmDialog';
import PrimaryActionButton from '../common/PrimaryActionButton';
import SalovareSelectField from '../common/SalovareSelectField';
import CustomerServicePackagesTable from './CustomerServicePackagesTable';
import CustomerServicePackageMobileCard from './CustomerServicePackageMobileCard';
import SellServicePackageDialog from './SellServicePackageDialog';
import { getServicePackageErrorKey } from './servicePackageErrors';
import { getServicePackageName } from './servicePackageDisplay';
import {
  CURRENT_PACKAGES_FILTER,
  CUSTOMER_PACKAGE_FILTERS,
  filterCustomerServicePackages,
  getCustomerPackageFilterLabelKey,
} from './customerServicePackageFilters';
import { useToast } from '../../context/ToastContext';
import usePermissions from '../../hooks/usePermissions';
import { Permissions } from '../../constants/Permissions';
import { allowsInStore } from '../../constants/CustomerPaymentMode';
import ServicePackageService, { isServicePackageActive } from '../../services/servicePackageService';
import requestDeduplicator from '../../utils/requestDeduplicator';
import { BRAND } from '../../theme/brandTokens';

const CustomerServicePackagesPanel = ({ storeId }) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { showError, showSuccess } = useToast();
  const { hasPermission } = usePermissions(storeId);
  const canSell = hasPermission(Permissions.ServicePackagesSell);

  const [customerPackages, setCustomerPackages] = useState([]);
  const [managementData, setManagementData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState(CURRENT_PACKAGES_FILTER);
  const [sellOpen, setSellOpen] = useState(false);
  const [selling, setSelling] = useState(false);
  const [sellErrorKey, setSellErrorKey] = useState(null);
  const [packageToCancel, setPackageToCancel] = useState(null);
  const [cancellingId, setCancellingId] = useState(null);

  const loadData = useCallback(async () => {
    if (!storeId) {
      setError('servicePackagesManagement.error.noStoreSelected');
      setLoading(false);
      return;
    }

    return requestDeduplicator.deduplicate(`customer-service-packages-panel-${storeId}-${canSell}`, async () => {
      try {
        setLoading(true);
        setError(null);
        const [packagesResult, managementResult] = await Promise.all([
          ServicePackageService.getCustomerPackagesManagement(storeId),
          canSell ? ServicePackageService.getServicePackageManagementData(storeId) : null,
        ]);
        setCustomerPackages(packagesResult);
        setManagementData(managementResult);
      } catch (loadError) {
        console.error('Failed to load customer service packages:', loadError);
        setError(getServicePackageErrorKey(loadError, 'servicePackagesManagement.customerPackages.error.loading'));
      } finally {
        setLoading(false);
      }
    });
  }, [canSell, storeId]);

  const refreshCustomerPackages = useCallback(async () => {
    try {
      setCustomerPackages(await ServicePackageService.getCustomerPackagesManagement(storeId));
    } catch (refreshError) {
      console.error('Failed to refresh customer service packages:', refreshError);
      showError(t('servicePackagesManagement.customerPackages.error.loading'));
    }
  }, [showError, storeId, t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const visiblePackages = useMemo(
    () => filterCustomerServicePackages(customerPackages, statusFilter),
    [customerPackages, statusFilter],
  );
  const activePackages = useMemo(
    () => (managementData?.packages || []).filter((servicePackage) => isServicePackageActive(servicePackage.status)),
    [managementData],
  );
  const showSellButton = canSell && Boolean(managementData);
  const inStoreSalesEnabled = showSellButton && allowsInStore(managementData.servicePackageCustomerPaymentMode);

  const handleSell = async ({ customerId, packageId, checkout }) => {
    try {
      setSelling(true);
      setSellErrorKey(null);
      await ServicePackageService.sellInStore(storeId, { customerId, packageId, checkout });
      setSellOpen(false);
      showSuccess(t('servicePackagesManagement.sell.success'));
      await refreshCustomerPackages();
    } catch (sellError) {
      console.error('Failed to sell service package:', sellError);
      setSellErrorKey(getServicePackageErrorKey(sellError, 'servicePackagesManagement.sell.error'));
    } finally {
      setSelling(false);
    }
  };

  const handleCancelPackage = async () => {
    const target = packageToCancel;
    if (!target) {
      return;
    }

    try {
      setCancellingId(target.id);
      await ServicePackageService.cancelCustomerPackage(storeId, target.id);
      showSuccess(t('servicePackagesManagement.customer.cancel.success'));
      await refreshCustomerPackages();
    } catch (cancelError) {
      console.error('Failed to cancel customer service package:', cancelError);
      showError(t(getServicePackageErrorKey(cancelError, 'servicePackagesManagement.customer.cancel.error')));
    } finally {
      setCancellingId(null);
      setPackageToCancel(null);
    }
  };

  if (loading) {
    return <ManagementContentSkeleton variant="detail" withAction={canSell} />;
  }

  if (error) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
        <Alert severity="error" sx={{ mb: 2 }}>{t(error)}</Alert>
        <Button id="customer-service-packages-retry" variant="contained" onClick={loadData}>
          {t('common.retry')}
        </Button>
      </Box>
    );
  }

  const hasPackages = customerPackages.length > 0;

  return (
    <Box id="customer-service-packages-content" sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
      {hasPackages || showSellButton ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'stretch', sm: 'flex-end' },
            gap: 2,
            mb: { xs: 2.5, sm: 4 },
            pt: { xs: 0.5, sm: 1 },
          }}
        >
          {hasPackages ? (
            <SalovareSelectField
              id="customer-service-packages-status-filter"
              label={t('servicePackagesManagement.customerPackages.filter.label')}
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
              sx={{ width: { xs: '100%', sm: 260 } }}
            >
              {CUSTOMER_PACKAGE_FILTERS.map((filter) => (
                <MenuItem key={filter} value={filter}>
                  {t(getCustomerPackageFilterLabelKey(filter))}
                </MenuItem>
              ))}
            </SalovareSelectField>
          ) : <Box />}

          {showSellButton ? (
            <Tooltip title={inStoreSalesEnabled ? '' : t('servicePackagesManagement.customer.sellUnavailable')}>
              <Box component="span" sx={{ display: 'inline-flex', width: { xs: '100%', sm: 'auto' } }}>
                <PrimaryActionButton
                  id="customer-service-packages-sell"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    setSellErrorKey(null);
                    setSellOpen(true);
                  }}
                  disabled={!inStoreSalesEnabled}
                  sx={{ px: { xs: 2.5, sm: 4 }, py: 1.5, width: { xs: '100%', sm: 'auto' } }}
                >
                  {t('servicePackagesManagement.customer.sell')}
                </PrimaryActionButton>
              </Box>
            </Tooltip>
          ) : null}
        </Box>
      ) : null}

      {!hasPackages ? (
        <Box id="customer-service-packages-empty" sx={{ textAlign: 'center', py: { xs: 4, sm: 6 } }}>
          <PackageIcon sx={{ fontSize: 48, color: BRAND.accentText, mb: 1.5 }} />
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
            {t('servicePackagesManagement.customerPackages.empty.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('servicePackagesManagement.customerPackages.empty.subtitle')}
          </Typography>
        </Box>
      ) : visiblePackages.length === 0 ? (
        <Typography
          id="customer-service-packages-empty-filtered"
          variant="body2"
          color="text.secondary"
          sx={{ textAlign: 'center', py: { xs: 4, sm: 6 } }}
        >
          {t('servicePackagesManagement.customerPackages.empty.filtered')}
        </Typography>
      ) : isMobile ? (
        <Box id="customer-service-packages-cards" sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {visiblePackages.map((customerPackage) => (
            <CustomerServicePackageMobileCard
              key={customerPackage.id}
              customerPackage={customerPackage}
              canCancel={canSell && customerPackage.canCancel}
              cancelling={cancellingId === customerPackage.id}
              onCancel={setPackageToCancel}
            />
          ))}
        </Box>
      ) : (
        <CustomerServicePackagesTable
          customerPackages={visiblePackages}
          canCancelPackages={canSell}
          cancellingId={cancellingId}
          onCancel={setPackageToCancel}
        />
      )}

      {showSellButton ? (
        <SellServicePackageDialog
          open={sellOpen}
          onClose={() => setSellOpen(false)}
          onSell={handleSell}
          selling={selling}
          activePackages={activePackages}
          services={managementData.services || []}
          currencyCode={managementData.currency}
          errorKey={sellErrorKey}
          storeId={storeId}
        />
      ) : null}

      <ConfirmDialog
        open={Boolean(packageToCancel)}
        onClose={() => setPackageToCancel(null)}
        onConfirm={handleCancelPackage}
        title={t('servicePackagesManagement.customer.cancel.title')}
        message={t('servicePackagesManagement.customer.cancel.message', {
          name: packageToCancel ? getServicePackageName(packageToCancel.name, i18n.language, t) : '',
        })}
        confirmText={t('servicePackagesManagement.customer.cancel.confirm')}
        cancelText={t('common.cancel')}
      />
    </Box>
  );
};

export default CustomerServicePackagesPanel;
